import React, { useEffect } from 'react';
import { useServicesStore } from '@/stores/servicesStore';
import { useTeamsStore } from '@/stores/teamsStore';
import { useUsersStore } from '@nimbitlabs/features';
import { useAuth } from '@nimbitlabs/auth';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@nimbitlabs/ui';
import { 
  Package, 
  Users, 
  UserCheck, 
  Activity, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  HelpCircle
} from 'lucide-react';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { services: allServices } = useServicesStore();
  const { teams: allTeams } = useTeamsStore();
  const { users, fetchUsers } = useUsersStore();

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);


  const availableServices = allServices.filter(s => s.status === 'Healthy').length;
  const downServices = allServices.filter(s => s.status === 'Error').length;
  const unknownServices = allServices.filter(s => s.status === 'Warning').length;
  const recentServices = allServices
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Healthy':
      case 'available':
        return <CheckCircle className="h-5 w-5 text-emerald-500" />;
      case 'Error':
      case 'down':
        return <XCircle className="h-5 w-5 text-rose-500" />;
      case 'Warning':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      default:
        return <HelpCircle className="h-5 w-5 text-slate-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Healthy':
      case 'available':
        return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 shadow-sm shadow-emerald-500/10';
      case 'Error':
      case 'down':
        return 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30 shadow-sm shadow-rose-500/10';
      case 'Warning':
        return 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 shadow-sm shadow-amber-500/10';
      default:
        return 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30';
    }
  };

  return (
    <div className="space-y-3 md:space-y-6 px-2 md:px-0">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Dashboard</h1>
        <p className="text-sm md:text-base text-muted-foreground">
          Overview of your service catalog and platform health
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-3 md:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <Card className="relative overflow-hidden border-purple-500/20 bg-gradient-to-br from-purple-500/5 via-transparent to-transparent hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl -z-10" />
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Services</CardTitle>
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-600/20 shadow-lg shadow-purple-500/20">
              <Package className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1 bg-gradient-to-br from-purple-600 to-purple-800 dark:from-purple-400 dark:to-purple-600 bg-clip-text text-transparent">{allServices.length}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-emerald-600 dark:text-emerald-400 font-medium">{availableServices} available</span>
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-blue-500/20 bg-gradient-to-br from-blue-500/5 via-transparent to-transparent hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl -z-10" />
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Teams</CardTitle>
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 shadow-lg shadow-blue-500/20">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1 bg-gradient-to-br from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">{allTeams.length}</div>
            <p className="text-sm text-muted-foreground">
              {allTeams.reduce((acc, team) => acc + team.members.length, 0)} total members
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 via-transparent to-transparent hover:shadow-lg hover:shadow-cyan-500/10 transition-all duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-3xl -z-10" />
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Users</CardTitle>
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 shadow-lg shadow-cyan-500/20">
              <UserCheck className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1 bg-gradient-to-br from-cyan-600 to-cyan-800 dark:from-cyan-400 dark:to-cyan-600 bg-clip-text text-transparent">{users.length}</div>
            <p className="text-sm text-muted-foreground">
              {users.filter(u => u.status === 'Active').length} active users
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl -z-10" />
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">System Health</CardTitle>
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 shadow-lg shadow-emerald-500/20">
              <Activity className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1 bg-gradient-to-br from-emerald-600 to-emerald-800 dark:from-emerald-400 dark:to-emerald-600 bg-clip-text text-transparent">
              {allServices.length > 0 ? ((availableServices / allServices.length) * 100).toFixed(1) : '0'}%
            </div>
            <p className="text-sm text-muted-foreground">
              {downServices} service{downServices !== 1 ? 's' : ''} down
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Service Health Overview */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="hover:shadow-md transition-shadow duration-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500/10 to-emerald-600/10">
                <Activity className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              Service Health Status
            </CardTitle>
            <CardDescription>
              Current status of all services in your catalog
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg hover:bg-emerald-500/5 transition-colors duration-200 group">
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                  <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="text-sm font-medium">Available</span>
              </div>
              <Badge variant="outline" className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 px-3 py-1 text-sm font-semibold shadow-sm shadow-emerald-500/10">
                {availableServices}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg hover:bg-rose-500/5 transition-colors duration-200 group">
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-lg bg-rose-500/10 group-hover:bg-rose-500/20 transition-colors">
                  <XCircle className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                </div>
                <span className="text-sm font-medium">Down</span>
              </div>
              <Badge variant="outline" className="bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30 px-3 py-1 text-sm font-semibold shadow-sm shadow-rose-500/10">
                {downServices}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg hover:bg-amber-500/5 transition-colors duration-200 group">
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-lg bg-amber-500/10 group-hover:bg-amber-500/20 transition-colors">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="text-sm font-medium">Unknown</span>
              </div>
              <Badge variant="outline" className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 px-3 py-1 text-sm font-semibold shadow-sm shadow-amber-500/10">
                {unknownServices}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-600/10">
                <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              Recent Activity
            </CardTitle>
            <CardDescription>
              Recently updated services
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentServices.map((service) => (
              <div key={service.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-accent/50 transition-all duration-200 group cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-background group- transition-transform duration-200">
                    {getStatusIcon(service.status)}
                  </div>
                  <div>
                    <p className="text-sm font-medium group-hover:text-primary transition-colors">{service.name}</p>
                    <p className="text-xs text-muted-foreground">{service.team}</p>
                  </div>
                </div>
                <Badge 
                  variant="outline" 
                  className={`${getStatusColor(service.status)} font-semibold px-2.5 py-0.5 text-xs`}
                >
                  {service.status}
                </Badge>
              </div>
            ))}
            <div className="pt-3">
              <Button variant="outline" size="sm" asChild className="w-full hover:bg-primary hover:text-primary-foreground transition-colors">
                <Link to="/services">View All Services</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common tasks and shortcuts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Button asChild>
              <Link to="/services">Browse Services</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/teams">Manage Teams</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/users">User Management</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
